import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useTracking } from '@project44/ui-utils';

import { ActionEnum } from '../../definitions/enums/ActionEnum';
import { EntityType } from '../../definitions/types/Entity';
import { StateType } from '../../definitions/types/StateType';
import { workspaceActionReducer } from '../../reducers/workspaceActionReducer';
import { SEARCH_URL } from '../../utils/constants/SEARCH_URL';
import { stateFromEntities } from '../../utils/helpers/stateFromEntities';
import { stateFromUrl } from '../../utils/helpers/stateFromUrl';

type ProviderProps = { children: ReactNode; entities?: EntityType[] };
type JumpOptions = { triggerMethod: string };

type WorkspaceActions = {
  state: StateType;
  add: (entity: EntityType) => void;
  remove: (entity: EntityType) => void;
  updateTab: (entity: EntityType) => void;
  next: (options: JumpOptions) => void;
  previous: (options: JumpOptions) => void;
  select: (entity: EntityType, options: JumpOptions) => void;
};

const WorkspaceActionsContext = createContext<WorkspaceActions | undefined>(
  undefined,
);

const WorkspaceActionsProvider = ({ children, entities }: ProviderProps) => {
  const location = useLocation();
  const trackEvents = useTracking();

  const workspaceClearUrl = useMemo(() => {
    const locationState = location.state as { workspaceClearUrl: string };

    if (locationState && locationState.workspaceClearUrl) {
      return locationState.workspaceClearUrl;
    }

    return SEARCH_URL;
  }, [location]);

  const initialState: StateType = entities
    ? stateFromEntities({
        url: `${location.pathname}${location.search}`,
        entities,
        workspaceClearUrl,
      })
    : stateFromUrl({
        url: `${location.pathname}${location.search}`,
        workspaceClearUrl,
      });

  const [state, dispatch] = useReducer(workspaceActionReducer, initialState);

  useEffect(() => {
    if (entities) return;

    dispatch({
      type: ActionEnum.parse,
      payload: `${location.pathname}${location.search}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const position = state.selected?.number
    ? parseInt(state.selected?.number, 10) - 1
    : 0;

  const removeEntity = (entity: EntityType) => {
    dispatch({ type: ActionEnum.remove, payload: entity });
  };

  const addEntity = async (entity: EntityType) => {
    dispatch({ type: ActionEnum.add, payload: entity });
  };

  const selectEntity = (entity: EntityType, options?: JumpOptions) => {
    const previousEntity = state.entities[position];
    if (entity === previousEntity) return;

    dispatch({ type: ActionEnum.select, payload: entity });

    if (previousEntity && options?.triggerMethod) {
      trackEvents.track({
        eventName: 'MVMT_WORKSPACE_NAVIGATE_TO_ENTITY',
        properties: {
          method: options.triggerMethod,
          fromEntityType: previousEntity.type,
          toEntityType: entity.type,
          totalWorkspaceEntities: state.entities.length,
          fromEntityPosition:
            state.entities.findIndex((e) => e.id === previousEntity.id) + 1,
          toEntityPosition:
            state.entities.findIndex((e) => e.id === entity.id) + 1,
        },
      });
    }
  };

  const updateEntityTab = (entity: EntityType) => {
    dispatch({ type: ActionEnum.updateTab, payload: entity });
  };

  const nextEntity = (options?: JumpOptions) => {
    if (position >= state.entities.length - 1) return;
    selectEntity(state.entities[position + 1], options);
  };

  const prevEntity = (options?: JumpOptions) => {
    if (position <= 0) return;
    selectEntity(state.entities[position - 1], options);
  };

  const value: WorkspaceActions = {
    state,
    remove: removeEntity,
    add: addEntity,
    select: selectEntity,
    updateTab: updateEntityTab,
    next: nextEntity,
    previous: prevEntity,
  };

  return (
    <WorkspaceActionsContext.Provider value={value}>
      {children}
    </WorkspaceActionsContext.Provider>
  );
};

function useWorkspaceActions() {
  const context = useContext(WorkspaceActionsContext);
  if (context === undefined) {
    throw new Error(
      'useWorkspaceActions must be used within a WorkspaceActionsProvider',
    );
  }
  return context;
}

export { useWorkspaceActions, WorkspaceActionsProvider };
